import { useId } from 'react'
import { cn } from '../../lib/cn.js'
import { Text } from './Typography.js'

/**
 * A label, a control, and whatever has to be said underneath.
 *
 * Wired together rather than placed next to each other: the label points at the
 * control, the hint and the error are announced with it, and an error replaces the
 * hint rather than stacking under it. Every screen that lays these out by hand gets
 * one of those three wrong.
 *
 * **The error is a string, not a boolean.** A field that knows it is wrong and
 * cannot say why is the thing every one of these screens is trying not to be.
 */
export interface FieldProps {
  label: React.ReactNode
  /** Under the control. Says what is expected, before anybody gets it wrong. */
  hint?: React.ReactNode
  /** Replaces the hint, and marks the control invalid. */
  error?: string | null
  /** Drawn beside the label, for the ones that genuinely are. */
  optional?: boolean
  /**
   * An asterisk beside the label, and "required" to a screen reader.
   *
   * For forms that mark both kinds (the professional's sign-up, client 2026-10-01:
   * "required fields should have a clear indicator such as an asterisk"). The desk's
   * own forms mark only the optional ones and leave this off.
   */
  required?: boolean
  /**
   * A name for this field, instead of the generated one.
   *
   * Only worth setting where something else has to *point* at the field —
   * `StillNeeded` turns "a registration link" into a press that scrolls to
   * it, and it needs an id it can write down before the field is rendered.
   * Everything else lets `useId` do it.
   */
  id?: string
  className?: string
  children: (props: {
    id: string
    'aria-describedby': string | undefined
    invalid: boolean
  }) => React.ReactNode
}

export function Field({
  label,
  hint,
  error,
  optional,
  required,
  id: given,
  className,
  children,
}: FieldProps) {
  const generated = useId()
  const id = given ?? generated
  const messageId = `${id}-message`
  const invalid = Boolean(error)
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {/* `body-strong`, not `body`. A label set in exactly the same weight as
          the text typed into the box beneath it makes a column of fields read
          as one grey paragraph; the half-step of weight is what separates the
          question from the answer. The gap tightens with it, because a label
          that reads as a label does not need distance to say so. */}
      <label htmlFor={id} className="flex items-baseline gap-2">
        <Text as="span" variant="body-strong">
          {label}
          {required && (
            <>
              <span aria-hidden className="ml-0.5 text-coral-text">
                *
              </span>
              <span className="sr-only"> (required)</span>
            </>
          )}
        </Text>
        {optional && (
          <Text as="span" variant="caption">
            Optional
          </Text>
        )}
      </label>
      {children({ id, 'aria-describedby': error || hint ? messageId : undefined, invalid })}
      {(error || hint) &&
        (error ? (
          /* Announced the moment it appears: somebody who has just pressed Save is
             not looking at the field they got wrong. `role` on `Text` is the type
             role, so the ARIA one goes on the element itself. */
          <Text
            id={messageId}
            as="span"
            variant="secondary"
            className="text-error"
            aria-live="polite"
          >
            {error}
          </Text>
        ) : (
          <Text id={messageId} as="span" variant="hint">
            {hint}
          </Text>
        ))}
    </div>
  )
}
